<script lang="ts" setup>
import type { CatalogPageData } from '~/types/Catalog';

const props = defineProps<{ pageData: CatalogPageData }>();

const isShowAllSeoLinks = ref(false);

onMounted(async () => {
  if (import.meta.client) {
    if (window.r46) window.r46('track', 'category', props.pageData.slug);
  }
});

const route = useRoute();

useHead({
  title: props.pageData.seo_data?.title,
  meta: [
    { name: 'description', content: props.pageData.seo_data?.description },
    { property: 'og:title', content: props.pageData.seo_data?.title },
    { property: 'og:description', content: props.pageData.seo_data?.description },
    { property: 'og:type', content: 'category' },
    { property: 'og:url', content: route.fullPath },
    { property: 'og:site_name', content: 'Hellride.ru' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:image', content: props.pageData.offers[0]?.offer_images[0]?.url },
  ],
});
</script>

<template>
  <div
    v-if="pageData"
    class="category-page"
  >
    <CatalogBreadcrumbsBlock :breadcrumbs="pageData.breadcrumbs" />

    <div class="flex items-center gap-2">
      <BackButton />

      <h1>
        {{ pageData.seo_data?.header || pageData.name }}
      </h1>
    </div>

    <section class="flex flex-col gap-6">
      <div
        v-if="pageData.sub_categories?.length"
        class="categories-grid"
      >
        <AppLink
          v-for="category in pageData.sub_categories"
          :key="category.slug"
          :to="category.uri"
        >
          <CategoryListItem
            :category="category"
            class="transition duration-200 shadow-lg hover:shadow-md hover:translate-y-0.5"
          />
        </AppLink>
      </div>

      <CategoryPageBanners
        v-if="pageData.banners.mobile && pageData.banners.desktop"
        :banners="pageData.banners"
      />

      <ProductsListing
        :filters="pageData.filters"
        :offers="pageData.offers"
        :pagination="pageData.pagination"
        :schema="pageData.schema"
        :sort-options="pageData.sorting_options"
      />

      <section
        v-if="pageData.seo_data?.links?.length"
        class="flex gap-2 flex-wrap mt-2"
      >
        <AppLink
          v-for="link in pageData.seo_data.links.slice(0, 12)"
          :key="link.id"
          :to="link.uri"
          class="category-page__seo-link"
        >
          {{ link.title }}
        </AppLink>

        <template v-if="isShowAllSeoLinks">
          <AppLink
            v-for="link in pageData.seo_data.links.slice(12)"
            :key="link.id"
            :to="link.uri"
            class="category-page__seo-link"
          >
            {{ link.title }}
          </AppLink>
        </template>

        <button
          v-if="pageData.seo_data.links.length > 12"
          class="category-page__seo-link-show-all-btn"
          @click="isShowAllSeoLinks = !isShowAllSeoLinks"
        >
          {{ isShowAllSeoLinks ? 'Скрыть' : 'Показать все' }}
        </button>
      </section>

      <FoldingTextBlock
        v-if="pageData.seo_data?.text"
        :max-length="220"
        :text="pageData.seo_data.text"
        class="text-sm"
      />
    </section>
  </div>
</template>

<style lang="scss" scoped>
.category-page {
  gap: 16px;

  @include inner-content;

  &__seo-link {
    padding: 4px 8px;
    border-radius: 8px;
    background-color: $white;
    color: $hell-black-80;
    font-size: $text-sm;
    transition: all 0.2s;

    @apply shadow-sm;

    &:hover {
      background-color: $hell-black-10;
      color: $hell-black;
    }
  }

  &__seo-link-show-all-btn {
    display: inline-flex;
    align-items: center;
    color: $hell-black-60;
    font-size: $text-sm;
    transition: all 0.2s;

    &:hover {
      color: $hell-black-40;
      text-decoration: underline;
    }
  }
}
</style>
